/**
 * @file Dashboard.tsx
 * @description Main dashboard view with branded heading, key feature cards, and SIS overlay.
 */

import { useEffect, useState } from "react";
import useSWR from "swr";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "../components/ui/card";
import { Button } from "../components/ui/button";

// Axios clients
const apiRoot = axios.create({ baseURL: "/api", withCredentials: true }); // backend API

// SWR fetcher for dashboard metrics
const fetchDash = (url: string) => apiRoot.get(url).then((r) => r.data);


function SISOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [sent, setSent] = useState(false);
  const [me, setMe] = useState<{ name?: string; email?: string } | null>(null);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);

    (async () => {
      try {
        const { data } = await apiRoot.get("/auth/me"); // lives under /api
        const name = data?.name || data?.full_name || data?.username || data?.user?.name;
        const email = data?.email || data?.user?.email;
        setMe({ name, email });
      } catch {
        setMe(null);
      }
    })();

    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleRequest = async () => {
    try {
      await apiRoot.post("/contact", {
        name: me?.name,
        email: me?.email,
        message:
          "User clicked 'Get More Information' about the SIS Integration from the dashboard overlay.",
      });
      setSent(true);
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Please try again.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      aria-modal="true"
      role="dialog"
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />

      <div className="relative bg-white rounded-2xl shadow-2xl max-w-xl w-[92%] p-8">
        <button
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
          onClick={onClose}
          aria-label="Close overlay"
        >
          ✕
        </button>

        <h2 className="text-2xl font-bold text-gray-900 mb-3">Supercharge Your Workflow</h2>

        <p className="text-gray-700 mb-4">
          Your Student Information System already knows every enrollment, course, and instructor
          update. Our <span className="font-semibold">SIS → LMS Integration</span> puts that data to
          work—creating new course shells, copying content, enrolling students, and syncing updates.
        </p>

        <ul className="list-disc ml-5 space-y-1 text-gray-700 mb-6">
          <li>
            <span className="font-medium">One-click course copy</span>: Duplicate content and update
            dates automatically.
          </li>
          <li>
            <span className="font-medium">Built-in safeguards</span>: Human review before go-live,
            with inline edits.
          </li>
          <li>
            <span className="font-medium">Always on time</span>: Works for rolling enrollments and
            fixed terms.
          </li>
          <li>
            <span className="font-medium">Instant sync</span>: SIS changes appear in Canvas as they
            happen.
          </li>
        </ul>

        {!sent ? (
          <Button onClick={handleRequest} className="bg-[#0A0F24] hover:bg-[#121a33]">
            Get More Information
          </Button>
        ) : (
          <p className="text-green-600 font-semibold">
            ✅ We’ve been notified and will be in touch soon!
          </p>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [showSIS, setShowSIS] = useState(false);

  // IMPORTANT: dashboard router is mounted with NO /api prefix → use /metrics/licenses
  const { data, isLoading, error } = useSWR<
  { total: number | string } | Array<{ total: number | string }>
>("/metrics/licenses", fetchDash);
  // Normalize backend variations: { total }, [{ total }], number or string
let rawTotal: number | string | undefined;

if (Array.isArray(data)) {
  rawTotal = data[0]?.total;
} else {
  rawTotal = data?.total;
}

const total =
  rawTotal !== undefined && rawTotal !== null && !Number.isNaN(Number(rawTotal))
    ? Number(rawTotal)
    : 0;


  return (
    <div className="p-10">
      <h1 className="text-2xl font-bold tracking-tight text-black mb-10">
        Infuse<span className="text-emerald-400">ED</span> Dashboard
        </h1>
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {/* Licenses Freed Up */}
        <Card className="flex flex-col justify-between">
          <CardHeader>
            <CardTitle>Licenses Freed Up</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-10 w-28 rounded-md bg-gray-200 animate-pulse" aria-label="Loading" />
            ) : (
              <p className="text-4xl font-extrabold text-primary">{total.toLocaleString()}</p>
            )}
            <p className="text-sm text-muted-foreground mt-1">
              Total Canvas licenses freed to date.
            </p>
            {error && (
              <p className="text-xs text-red-600 mt-2">
                Couldn’t load metric. Showing 0 for now.
              </p>
            )}
          </CardContent>
          <CardFooter>
            <Button variant="outline" disabled>
              Data Updates Automatically
            </Button>
          </CardFooter>
        </Card>

        {/* SIS Integration Promo */}
        <Card className="flex flex-col justify-between">
          <CardHeader>
            <CardTitle>SIS Integration</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              See how easily your SIS can sync with Canvas using our custom integration tool.
            </p>
          </CardContent>
          <CardFooter>
            <Button onClick={() => setShowSIS(true)}>Learn More</Button>
          </CardFooter>
        </Card>

        {/* Field Guide AI Teaser */}
        <Card className="flex flex-col justify-between">
          <CardHeader>
            <CardTitle>Coming Soon: Field Guide AI</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Our early-warning system for advisors to keep students engaged. Stay tuned!
            </p>
          </CardContent>
          <CardFooter>
            <Button variant="secondary" disabled>
              Launching Soon
            </Button>
          </CardFooter>
        </Card>
      </div>

      <SISOverlay open={showSIS} onClose={() => setShowSIS(false)} />
    </div>
  );
}
